import type { HTMLAttributes, ReactNode } from 'react'
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react'
import { Button } from './Button'

// `title` on HTMLAttributes is the DOM tooltip string; Alert repurposes it as a
// heading node, so it is omitted from the spread type. `icon` controls the
// leading glyph: undefined → the default per-variant icon; a node → that node;
// null → no icon (for icon-less banners / progress toasts).
export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children: ReactNode
  variant?: 'info' | 'success' | 'warning' | 'error'
  title?: string
  icon?: ReactNode
  onClose?: () => void
  className?: string
}

export function Alert({ children, variant = 'info', title, icon, onClose, className = '', ...rest }: AlertProps) {
  const variantClasses = {
    info: 'alert-info',
    success: 'alert-success',
    warning: 'alert-warning',
    error: 'alert-error',
  }

  const defaultIcons = {
    info: <Info className="w-5 h-5" />,
    success: <CheckCircle className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    error: <AlertCircle className="w-5 h-5" />,
  }

  // undefined → default variant icon; null → no icon; node → custom icon
  const resolvedIcon = icon === undefined ? defaultIcons[variant] : icon

  return (
    <div className={`alert ${variantClasses[variant]} ${className}`.trim()} {...rest}>
      {resolvedIcon}
      <div className="flex-1">
        {title && <h3 className="font-bold">{title}</h3>}
        <div className="text-sm">{children}</div>
      </div>
      {onClose && (
        <Button variant="ghost" size="sm" shape="circle" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  )
}
